import { t } from "@lingui/macro";
import {
  type BudgetItemPlannedExpenseInput,
  type ExpenseBudgetItemTypeEnum,
  ProjectDetailBudgetItemPlannedExpensesDocument,
  type ProjectDetailBudgetItemPlannedExpensesQuery,
  type ProjectDetailBudgetItemPlannedExpensesQueryVariables,
  ProjectDetailCreateBudgetItemPlannedExpensesBulkDocument,
  type ProjectDetailCreateBudgetItemPlannedExpensesBulkMutation,
  type ProjectDetailCreateBudgetItemPlannedExpensesBulkMutationVariables,
  ProjectDetailCreateBudgetItemPlannedExpensesDocument,
  type ProjectDetailCreateBudgetItemPlannedExpensesMutation,
  type ProjectDetailCreateBudgetItemPlannedExpensesMutationVariables,
  type ProjectDetailPlannedExpenseFieldsFragment,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { NumberModel } from "@src/utils/models";
import { FieldState, FormState } from "formstate";
import { uniqueId } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export enum PlannedExpensesAmountMode {
  BuyPrice = "BUY_PRICE",
  ClientPrice = "CLIENT_PRICE",
}

export type ProjectPlannedExpensesModalItem = {
  id: string;
  title: string;
  budgetItemType: ExpenseBudgetItemTypeEnum;
  targetClientAmount: number;
  targetBuyPriceAmount?: number;
};

type ProjectPlannedExpensesModalData = {
  item: ProjectPlannedExpensesModalItem;
  visibleBudgetItemIds: string[];
  onSaved: (data: {
    budgetItemIds: string[];
    hasPlannedExpenses: boolean;
  }) => void;
};

const currencyDenomination =
  appStore.workspaceStore.settings?.currency.denomination ?? 2;

type PlannedExpenseRow = {
  id?: ProjectDetailPlannedExpenseFieldsFragment["id"];
  description:
    | NonNullable<ProjectDetailPlannedExpenseFieldsFragment["description"]>
    | undefined;
  contractorId: NonNullable<
    ProjectDetailPlannedExpenseFieldsFragment["contractor"]
  >["id"];
  date: Date | undefined;
  buyPrice: NumberModel;
  clientPrice: NumberModel;
  hasAnyValue?: boolean;
};

type PlannedExpenseRowDefaults = {
  buyPrice?: number;
  clientPrice?: number;
};

class PlannedExpenseRowForm extends FormState<
  TToFieldStates<Omit<PlannedExpenseRow, "hasAnyValue">>
> {
  readonly key: string;

  contractorName?: string;

  constructor(
    src?: ProjectDetailPlannedExpenseFieldsFragment,
    defaults?: PlannedExpenseRowDefaults,
  ) {
    super({
      id: new FieldState<string | undefined>(src?.id),
      description: new FieldState(src?.description ?? undefined),
      contractorId: new FieldState(src?.contractor?.id ?? ""),
      date: new FieldState<Date | undefined>(
        src?.date ? new Date(src.date) : undefined,
      ),
      buyPrice: new FieldState(
        new NumberModel(
          src?.budgeted_amount ?? defaults?.buyPrice ?? 0,
          currencyDenomination,
        ),
      ).validators(required),
      clientPrice: new FieldState(
        new NumberModel(
          src?.client_amount ?? defaults?.clientPrice ?? 0,
          currencyDenomination,
        ),
      ).validators(required),
    });

    makeObservable(this, {
      contractorName: observable.ref,
      handleClientPriceBlur: action.bound,
    });

    this.key = uniqueId("planned-expense-row-");
    this.contractorName = src?.contractor?.legal_name;
  }

  handleClientPriceBlur() {
    if (this.$.buyPrice.value.denominatedValue > 0) return;

    this.$.buyPrice.reset(
      this.$.buyPrice.value.setDenomValue(
        this.$.clientPrice.value.denominatedValue,
      ),
    );
  }

  async serialize(
    includeId = true,
  ): Promise<BudgetItemPlannedExpenseInput | null> {
    const res = await this.validate();
    if (res.hasError) return null;

    return {
      id: includeId ? res.value.id.$ : undefined,
      description: res.value.description.value?.trim(),
      budgeted_amount: res.value.buyPrice.$.denominatedValue,
      client_amount: res.value.clientPrice.$.denominatedValue,
      date: res.value.date.$ ? toApiDate(res.value.date.$) : null,
      contractor_id: res.value.contractorId.$,
    };
  }
}

export class PlannedExpensesModalStore {
  @observable.shallow
  rows: PlannedExpenseRowForm[] = [];

  @observable.ref
  amountMode = PlannedExpensesAmountMode.BuyPrice;

  readonly modalState = new DisclosureState<ProjectPlannedExpensesModalData>({
    onOpen: (additionalData) => {
      if (!additionalData) return;
      this.rows = [];
      this.amountMode =
        additionalData.item.targetBuyPriceAmount == null
          ? PlannedExpensesAmountMode.ClientPrice
          : PlannedExpensesAmountMode.BuyPrice;
      void this.fetchRows(
        additionalData.item.id,
        additionalData.item.budgetItemType,
      );
    },
    onClose: () => {
      this.rows = [];
      this.amountMode = PlannedExpensesAmountMode.BuyPrice;
      this.applyToAllDisclosure.onClose();
    },
  });

  readonly applyToAllDisclosure = new DisclosureState({
    onClose: () => undefined,
  });

  private readonly fetcher = new FetchHelper<
    ProjectDetailBudgetItemPlannedExpensesQuery,
    ProjectDetailBudgetItemPlannedExpensesQueryVariables
  >(ProjectDetailBudgetItemPlannedExpensesDocument);

  private readonly saveMutator = new MutationHelper<
    ProjectDetailCreateBudgetItemPlannedExpensesMutation,
    ProjectDetailCreateBudgetItemPlannedExpensesMutationVariables
  >(ProjectDetailCreateBudgetItemPlannedExpensesDocument);

  private readonly bulkMutator = new MutationHelper<
    ProjectDetailCreateBudgetItemPlannedExpensesBulkMutation,
    ProjectDetailCreateBudgetItemPlannedExpensesBulkMutationVariables
  >(ProjectDetailCreateBudgetItemPlannedExpensesBulkDocument);

  constructor() {
    makeObservable(this);
  }

  @computed
  get isLoading() {
    return this.fetcher.isLoading.value;
  }

  @computed
  get isSaving() {
    return this.saveMutator.isLoading.value || this.bulkMutator.isLoading.value;
  }

  @computed
  get selectedItem() {
    return this.modalState.additionalData?.item;
  }

  @computed
  get visibleBudgetItemIds() {
    return this.modalState.additionalData?.visibleBudgetItemIds ?? [];
  }

  @computed
  get hasMultipleVisibleBudgetItems() {
    return this.visibleBudgetItemIds.length > 1;
  }

  @computed
  get canSave() {
    return !!this.selectedItem && !this.isLoading && !this.isSaving;
  }

  @computed
  get canApplyToAll() {
    return (
      this.hasMultipleVisibleBudgetItems && !this.isLoading && !this.isSaving
    );
  }

  @computed
  get targetAmount() {
    if (!this.selectedItem) return 0;

    if (this.amountMode === PlannedExpensesAmountMode.ClientPrice) {
      return this.selectedItem.targetClientAmount;
    }

    return this.selectedItem.targetBuyPriceAmount;
  }

  @computed
  get totalAmount() {
    return this.rows.reduce((prev, row) => {
      if (this.amountMode === PlannedExpensesAmountMode.ClientPrice) {
        return prev + row.$.clientPrice.value.denominatedValue;
      }

      return prev + row.$.buyPrice.value.denominatedValue;
    }, 0);
  }
  @computed
  get remainingBuyPriceAmount() {
    if (!this.selectedItem || this.selectedItem.targetBuyPriceAmount == null) {
      return undefined;
    }

    const totalBuyPrice = this.rows.reduce((sum, row) => {
      return sum + row.$.buyPrice.value.denominatedValue;
    }, 0);

    return this.selectedItem.targetBuyPriceAmount - totalBuyPrice;
  }

  @computed
  get remainingClientPriceAmount() {
    if (!this.selectedItem) return 0;

    const totalClientPrice = this.rows.reduce((sum, row) => {
      return sum + row.$.clientPrice.value.denominatedValue;
    }, 0);

    return this.selectedItem.targetClientAmount - totalClientPrice;
  }

  @computed
  get remainingAmount() {
    if (this.amountMode === PlannedExpensesAmountMode.ClientPrice) {
      return this.remainingClientPriceAmount;
    }

    return this.remainingBuyPriceAmount;
  }

  @computed
  get hasRemainingAmount() {
    return this.remainingAmount != null;
  }

  @computed
  get amountModeOptions() {
    return [
      {
        label: t`Buy price`,
        value: PlannedExpensesAmountMode.BuyPrice,
      },
      {
        label: t`Client price`,
        value: PlannedExpensesAmountMode.ClientPrice,
      },
    ];
  }

  @action.bound
  addRow(useRemainingAmount = true) {
    this.rows.push(
      new PlannedExpenseRowForm(
        undefined,
        useRemainingAmount
          ? {
              buyPrice: this.remainingBuyPriceAmount ?? 0,
              clientPrice: this.remainingClientPriceAmount,
            }
          : undefined,
      ),
    );
  }

  @action.bound
  removeRow(key: string) {
    this.rows = this.rows.filter((row) => row.key !== key);

    if (this.rows.length > 0) return;
    this.addRow(false);
  }

  @action.bound
  setAmountMode(value: PlannedExpensesAmountMode) {
    this.amountMode = value;
  }

  @action.bound
  async fetchRows(
    budgetItemId: string,
    budgetItemType: ExpenseBudgetItemTypeEnum,
  ) {
    const [data, error] = await this.fetcher.fetch({
      budgetItemId,
      budgetItemType,
    });

    if (error) return;
    this.rows =
      data.budgetItemPlannedExpenses.map(
        (row) => new PlannedExpenseRowForm(row),
      ) ?? [];

    if (this.rows.length > 0) return;
    this.addRow();
  }

  @action.bound
  async save() {
    if (!this.canSave || !this.selectedItem) return;
    const serializedRows = await this.serializeRows();
    if (!serializedRows) return;

    const [data, error] = await this.saveMutator.mutate({
      input: {
        budget_item_id: this.selectedItem.id,
        budget_item_type: this.selectedItem.budgetItemType,
        planned_expenses: serializedRows,
      },
    });

    if (error || !data?.createBudgetItemPlannedExpenses) return;

    appStore.UIStore.toast({
      title: t`Planned expenses were updated.`,
      status: "success",
    });
    this.modalState.additionalData?.onSaved({
      budgetItemIds: [this.selectedItem.id],
      hasPlannedExpenses: serializedRows.length > 0,
    });
    this.modalState.onClose();
  }

  @action.bound
  async applyToAll() {
    if (!this.canApplyToAll) return false;
    const selectedItem = this.selectedItem;
    if (!selectedItem) return false;
    const serializedRows = await this.serializeRows(false);
    if (serializedRows === null) return;

    const [data, error] = await this.bulkMutator.mutate({
      items: this.visibleBudgetItemIds.map((budgetItemId) => ({
        budget_item_id: budgetItemId,
        budget_item_type: selectedItem.budgetItemType,
        planned_expenses: serializedRows,
      })),
    });

    if (error || !data?.createBudgetItemPlannedExpensesBulk) return;

    appStore.UIStore.toast({
      title: t`Planned expenses were applied to all budget items.`,
      status: "success",
    });
    this.applyToAllDisclosure.onClose();
    this.modalState.additionalData?.onSaved({
      budgetItemIds: this.visibleBudgetItemIds,
      hasPlannedExpenses: serializedRows.length > 0,
    });
    this.modalState.onClose();
    return;
  }

  private async serializeRows(
    includeIds = true,
  ): Promise<BudgetItemPlannedExpenseInput[] | null> {
    const rows: BudgetItemPlannedExpenseInput[] = [];

    for (const row of this.rows) {
      const res = await row.serialize(includeIds);
      if (!res) continue;

      const hasAnyValue =
        (res.description && res.description.length > 0) ||
        !!res.contractor_id ||
        !!res.date ||
        res.budgeted_amount > 0 ||
        res.client_amount > 0;

      if (!hasAnyValue) continue;

      rows.push(res);
    }

    return rows;
  }
}
